<template>
  <div class="audit-logs" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Audit Logs</h1>
        <p class="page-subtitle">Track all administrative actions across the platform</p>
      </div>
      <button @click="refreshLogs" class="btn-refresh" :disabled="loading">
        <i class="pi pi-refresh" :class="{ 'spinning': loading }"></i>
        Refresh
      </button>
    </div>

    <!-- Filters (must match GET /platform/audit query params) -->
    <div class="filters-bar">
      <div class="search-box">
        <i class="pi pi-building"></i>
        <input
          v-model="targetTenantFilter"
          type="text"
          placeholder="Filter by target tenant slug (optional)..."
          @input="debouncedLoad"
        />
      </div>
      <select v-model="actionFilter" @change="() => loadLogs(false)" class="filter-select">
        <option value="">All actions</option>
        <option
          v-for="opt in auditActionOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
    </div>

    <!-- Loading: show spinner whenever refetching filters (list cleared first), or initial load -->
    <div v-if="loading && !loadingMore" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Loading audit logs...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="logs.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="pi pi-history"></i>
      </div>
      <h3>No Audit Logs Found</h3>
      <p>{{ listError || 'No rows match the current filters, or nothing has been logged yet.' }}</p>
    </div>

    <!-- Logs Timeline -->
    <div v-else class="logs-timeline">
      <div
        v-for="log in logs"
        :key="log._id"
        class="log-entry"
        :class="getActionClass(log.action)"
      >
        <div class="log-icon">
          <i :class="getActionIcon(log.action)"></i>
        </div>
        <div class="log-content">
          <div class="log-header">
            <span class="log-action">{{ formatAction(log.action) }}</span>
            <span class="log-entity">{{ log.targetUser || '—' }}</span>
            <span class="log-time">{{ formatTime(log.timestamp) }}</span>
          </div>
          <p class="log-description">{{ log.performedByEmail || formatAction(log.action) }}</p>
          <div class="log-meta">
            <span class="log-user">
              <i class="pi pi-user"></i>
              {{ log.performedBy }}
            </span>
            <span v-if="log.targetTenant" class="log-tenant">
              <i class="pi pi-building"></i>
              {{ log.targetTenant }}
            </span>
            <span v-if="log.ipAddress" class="log-ip">
              <i class="pi pi-globe"></i>
              {{ log.ipAddress }}
            </span>
          </div>
          <div v-if="log.details && Object.keys(log.details).length > 0" class="log-details">
            <button @click="toggleDetails(log._id)" class="details-toggle">
              <i
                :class="expandedLogs.includes(log._id)
                  ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
              ></i>
              {{ expandedLogs.includes(log._id) ? 'Hide Details' : 'Show Details' }}
            </button>
            <pre
              v-if="expandedLogs.includes(log._id)"
              class="details-content"
            >{{ JSON.stringify(log.details, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More -->
    <div v-if="hasMore" class="load-more">
      <button @click="loadMore" class="btn-load-more" :disabled="loading">
        <i v-if="loading" class="pi pi-spin pi-spinner"></i>
        {{ loading ? 'Loading...' : 'Load More' }}
      </button>
    </div>
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';

/** Keep in sync with ALL_AUDIT_ACTIONS in omind-backend-monorepo/.../platform-audit.service.ts */
const PLATFORM_AUDIT_ACTION_VALUES = [
  'DEFAULT_CONTENT_RESET',
  'DEFAULT_CONTENT_UPDATED',
  'KNOWLEDGE_ARTICLES_REORDERED',
  'KNOWLEDGE_ARTICLE_CREATED',
  'KNOWLEDGE_ARTICLE_DELETED',
  'KNOWLEDGE_ARTICLE_UPDATED',
  'PLATFORM_ADMIN_LOGIN',
  'PLATFORM_ADMIN_LOGIN_FAILED',
  'PLATFORM_MCP_TOKEN_CREATED',
  'PLATFORM_MCP_TOKEN_DELETED',
  'PLATFORM_MCP_TOKEN_REVOKED',
  'PLATFORM_USER_CREATED',
  'PLATFORM_USER_DELETED',
  'PLATFORM_USER_UPDATED',
  'SETTINGS_UPDATED',
  'TENANT_ACTIVATED',
  'TENANT_CREATED',
  'TENANT_DELETED',
  'TENANT_EXPORTED',
  'TENANT_IMPORTED',
  'TENANT_IMPORT_FAILED',
  'TENANT_IMPERSONATED',
  'TENANT_IMPERSONATION_ENDED',
  'TENANT_SUSPENDED',
  'TENANT_UPDATED',
  'TENANT_ROLES_MIGRATED',
  'TENANT_FEATURES_MIGRATED',
  'TENANT_CUSTOMER_LAYOUTS_SEEDED',
  'TRAINER_ASSIGNED',
  'TRAINER_UNASSIGNED',
];

function humanizeAuditAction(action) {
  if (!action) return '';
  return String(action)
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export default {
  name: 'AuditLogs',
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      /** True only when appending the next page; keeps table visible with spinner in button */
      loadingMore: false,
      logs: [],
      listError: '',
      targetTenantFilter: '',
      actionFilter: '',
      currentPage: 1,
      hasMore: true,
      expandedLogs: [],
      debounceTimer: null,
      pageSize: 20,
    };
  },
  computed: {
    auditActionOptions() {
      return PLATFORM_AUDIT_ACTION_VALUES.map((value) => ({
        value,
        label: humanizeAuditAction(value),
      }));
    },
  },
  mounted() {
    this.loadLogs(false);
  },
  methods: {
    async loadLogs(append) {
      const isAppend = append === true;
      this.listError = '';
      if (!isAppend) {
        this.logs = [];
        this.loadingMore = false;
      } else {
        this.loadingMore = true;
      }
      this.loading = true;
      try {
        const params = {
          page: isAppend ? this.currentPage : 1,
          limit: this.pageSize,
        };
        if (this.actionFilter) {
          params.action = this.actionFilter;
        }
        const tenant = this.targetTenantFilter?.trim();
        if (tenant) {
          params.targetTenant = tenant;
        }

        const batch = await platformApi.getAuditLogs(params);
        const rows = Array.isArray(batch) ? batch : [];

        if (isAppend) {
          this.logs = [...this.logs, ...rows];
        } else {
          this.logs = rows;
          this.currentPage = 1;
        }
        this.hasMore = rows.length >= this.pageSize;
      } catch (error) {
        console.error('Failed to load audit logs:', error);
        const msg = error?.response?.data?.message || error?.message || 'Could not load audit logs.';
        this.listError = msg;
        if (!isAppend) {
          this.logs = [];
        } else {
          this.currentPage = Math.max(1, this.currentPage - 1);
        }
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    loadMore() {
      this.currentPage += 1;
      this.loadLogs(true);
    },
    refreshLogs() {
      this.loadLogs(false);
    },
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.loadLogs(false);
      }, 300);
    },
    toggleDetails(logId) {
      if (this.expandedLogs.includes(logId)) {
        this.expandedLogs = this.expandedLogs.filter((id) => id !== logId);
      } else {
        this.expandedLogs.push(logId);
      }
    },
    getActionClass(action) {
      const a = String(action || '');
      if (/_CREATED$|_IMPORTED$/.test(a)) return 'action-create';
      if (/_UPDATED$|_REORDERED$|^SETTINGS_UPDATED$|^DEFAULT_CONTENT_UPDATED$/.test(a)) return 'action-update';
      if (/_DELETED$|_REVOKED$/.test(a)) return 'action-delete';
      if (/_SUSPENDED$/.test(a)) return 'action-suspend';
      if (/_ACTIVATED$/.test(a)) return 'action-activate';
      if (/LOGIN/.test(a)) return 'action-login';
      if (/_EXPORTED$|IMPERSONAT/.test(a)) return 'action-access';
      return 'action-default';
    },
    getActionIcon(action) {
      const a = String(action || '');
      if (/_CREATED$|_IMPORTED$/.test(a)) return 'pi pi-plus-circle';
      if (/_UPDATED$|_REORDERED$|SETTINGS|DEFAULT_CONTENT/.test(a)) return 'pi pi-pencil';
      if (/_DELETED$/.test(a)) return 'pi pi-trash';
      if (/_SUSPENDED$/.test(a)) return 'pi pi-ban';
      if (/_ACTIVATED$/.test(a)) return 'pi pi-check-circle';
      if (/LOGIN/.test(a)) return 'pi pi-sign-in';
      if (/_EXPORTED$|IMPERSONAT/.test(a)) return 'pi pi-download';
      return 'pi pi-info-circle';
    },
    formatAction(action) {
      return humanizeAuditAction(action);
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;

      // Less than 1 hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return minutes <= 1 ? 'Just now' : `${minutes} min ago`;
      }

      // Less than 24 hours
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }

      // Less than 7 days
      if (diff < 604800000) {
        const days = Math.floor(diff / 86400000);
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }

      // Otherwise show full date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
/* Light Theme (Default) */
.audit-logs {
  --text-primary: #1E2019;
  --text-secondary: #666666;
  --text-muted: #64748b;
  --bg-card: #ffffff;
  --bg-input: #f8f8f8;
  --border-color: #e7e7e7;
  --border-item: #f8f8f8;
  --accent-border: rgba(228, 130, 26, 0.2);
  --timeline-line: #e7e7e7;

  max-width: 1000px;
  margin: 0 auto;
  transition: all 0.3s ease;
}

/* Dark Theme */
.audit-logs.dark-theme {
  --text-primary: #f5f5f5;
  --text-secondary: #a8a8a0;
  --text-muted: #777777;
  --bg-card: #2a2a24;
  --bg-input: #353530;
  --border-color: rgba(228, 130, 26, 0.2);
  --border-item: rgba(228, 130, 26, 0.1);
  --accent-border: rgba(228, 130, 26, 0.3);
  --timeline-line: rgba(228, 130, 26, 0.2);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--text-secondary);
  margin-top: 0.5rem;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: rgba(228, 130, 26, 0.1);
  border-color: rgba(228, 130, 26, 0.5);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spinning {
  animation: spin 1s linear infinite;
}

/* Filters */
.filters-bar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 280px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.search-box input:focus {
  outline: none;
  border-color: rgba(228, 130, 26, 0.5);
}

.filter-select {
  padding: 0.75rem 1rem;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: rgba(228, 130, 26, 0.5);
}

.filter-select option {
  background: var(--bg-card);
  color: var(--text-primary);
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: var(--bg-card);
  border: 1px solid var(--accent-border);
  border-radius: 1rem;
}

.loading-state {
  flex-direction: row;
  gap: 1rem;
  color: #e4821a;
}

.empty-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.1);
  border-radius: 50%;
  margin-bottom: 1rem;
}

.empty-icon i {
  font-size: 2rem;
  color: #e4821a;
}

.empty-state h3 {
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0;
}

/* Logs Timeline */
.logs-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.log-entry {
  display: flex;
  gap: 1rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid var(--border-item);
  position: relative;
}

.log-entry::before {
  content: '';
  position: absolute;
  left: 19px;
  top: 48px;
  bottom: 0;
  width: 2px;
  background: var(--timeline-line);
}

.log-entry:last-child::before {
  display: none;
}

.log-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.action-create .log-icon {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.action-update .log-icon {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.action-delete .log-icon {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.action-access .log-icon {
  background: rgba(228, 130, 26, 0.2);
  color: #a78bfa;
}

.action-login .log-icon {
  background: rgba(34, 211, 238, 0.2);
  color: #22d3ee;
}

.action-suspend .log-icon {
  background: rgba(234, 179, 8, 0.2);
  color: #fbbf24;
}

.action-activate .log-icon {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.action-default .log-icon {
  background: rgba(148, 163, 184, 0.2);
  color: #666666;
}

.log-content {
  flex: 1;
  min-width: 0;
}

.log-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

.log-action {
  font-weight: 600;
  color: var(--text-primary);
}

.log-entity {
  padding: 0.125rem 0.5rem;
  background: rgba(228, 130, 26, 0.1);
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #e4821a;
}

.log-time {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-left: auto;
}

.log-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
}

.log-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.log-user,
.log-tenant,
.log-ip {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: var(--text-muted);
}

.log-user i,
.log-tenant i,
.log-ip i {
  font-size: 0.75rem;
}

.log-details {
  margin-top: 0.75rem;
}

.details-toggle {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.details-toggle:hover {
  background: rgba(228, 130, 26, 0.1);
  border-color: rgba(228, 130, 26, 0.3);
  color: var(--text-primary);
}

.details-content {
  margin-top: 0.75rem;
  padding: 1rem;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Load More */
.load-more {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.btn-load-more {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  background: rgba(228, 130, 26, 0.1);
  border: 1px solid rgba(228, 130, 26, 0.3);
  border-radius: 0.75rem;
  color: #e4821a;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-load-more:hover:not(:disabled) {
  background: rgba(228, 130, 26, 0.2);
  border-color: rgba(228, 130, 26, 0.5);
}

.btn-load-more:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
